import {
  deriveNoncanonicalLoomStoredPoseMigrationRevision,
  deriveLoomWingControlSurfacePoseRevision,
  parseLoomWingControlSurfacePose,
  type LoomSha256Revision,
  type LoomWingControlSurfaceCollection,
  type LoomWingControlSurfacePose,
} from "./loomWingControlSurfaceContract";

interface StoredPoseReceipt {
  readonly pose: LoomWingControlSurfacePose;
  readonly poseRevision: LoomSha256Revision;
}

export interface RestoredLoomControlSurfacePose {
  readonly pose: LoomWingControlSurfacePose;
  readonly staleDefinition: boolean;
}

function storedWrapper(raw: string): Record<string, unknown> {
  const value = JSON.parse(raw) as unknown;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("stored pose receipt must be an object");
  }
  const wrapper = value as Record<string, unknown>;
  if (Object.keys(wrapper).sort().join(",") !== "pose,poseRevision") {
    throw new Error("stored pose receipt has unknown fields");
  }
  if (typeof wrapper.poseRevision !== "string" || !/^sha256:[0-9a-f]{64}$/.test(wrapper.poseRevision)) {
    throw new Error("stored pose receipt revision is invalid");
  }
  return wrapper;
}

export async function parseStoredLoomControlSurfacePoseReceipt(
  raw: string,
  collection: LoomWingControlSurfaceCollection,
  currentDefinitionRevision: LoomSha256Revision,
): Promise<RestoredLoomControlSurfacePose> {
  const wrapper = storedWrapper(raw);
  const rawPose = wrapper.pose as Record<string, unknown>;
  const storedDefinitionRevision = rawPose?.definitionRevision;
  if (typeof storedDefinitionRevision !== "string" || !/^sha256:[0-9a-f]{64}$/.test(storedDefinitionRevision)) {
    throw new Error("stored pose definition revision is invalid");
  }
  const pose = parseLoomWingControlSurfacePose(
    rawPose, collection, storedDefinitionRevision as LoomSha256Revision,
  );
  const actualRevision = await (storedDefinitionRevision === currentDefinitionRevision
    ? deriveLoomWingControlSurfacePoseRevision(collection, pose)
    : deriveNoncanonicalLoomStoredPoseMigrationRevision(
      collection, storedDefinitionRevision as LoomSha256Revision, pose,
    ));
  if (wrapper.poseRevision !== actualRevision) {
    throw new Error("stored pose receipt does not match its canonical pose");
  }
  return { pose, staleDefinition: storedDefinitionRevision !== currentDefinitionRevision };
}

export async function serializeLoomControlSurfacePoseReceipt(
  collection: LoomWingControlSurfaceCollection,
  pose: LoomWingControlSurfacePose,
): Promise<string> {
  const wrapper: StoredPoseReceipt = {
    pose,
    poseRevision: await deriveLoomWingControlSurfacePoseRevision(collection, pose),
  };
  return JSON.stringify(wrapper);
}
